import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { DetailTableLayout } from '@/components/common/DetailTableLayout';
import { TableTabs, type TableTab } from '@/components/common/TableTabs';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import type { ViewedRowList } from '@/store/lastViewedRow.store';
import { workSummaryQueryOptions } from '../api/dashboards.api';
import {
  getWorkSummaryDcrColumns,
  getWorkSummaryExpenseColumns,
} from '../components/WorkSummaryColumns';
import { DCR_TYPE_COLUMNS } from '../constants/dashboard.constants';
import { getWorkSummaryStatCards } from '../utils/workSummaryStatCards';

const SUMMARY_NAME = 'Work Summary';
const ERROR_TITLE = 'Could not load the work summary';

type WorkSummaryTab = 'dcr' | 'expenses';

interface WorkSummaryPeriod {
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
}

interface WorkSummaryPageProps {
  userId: number;
  period: WorkSummaryPeriod;
  dcrTypeId?: number | undefined;
  viewedRowList: ViewedRowList;
  onNoHistory: () => void;
}

function getWorkTypeTitle(dcrTypeId: number | undefined): string | undefined {
  if (dcrTypeId === undefined) return undefined;
  return DCR_TYPE_COLUMNS.find((column) => column.id === dcrTypeId)?.title;
}

function getPageTitle(memberName: string | undefined, workTypeTitle: string | undefined): string {
  const summaryName = workTypeTitle ?? SUMMARY_NAME;
  return memberName ? `${memberName} - ${summaryName}` : summaryName;
}

export function WorkSummaryPage({
  userId,
  period,
  dcrTypeId,
  viewedRowList,
  onNoHistory,
}: WorkSummaryPageProps) {
  const [activeTab, setActiveTab] = useState<WorkSummaryTab>('dcr');

  useRememberViewedRow(viewedRowList, userId);

  const summaryQuery = useQuery(
    workSummaryQueryOptions(userId, {
      dateFrom: period.dateFrom,
      dateTo: period.dateTo,
      dcrTypeId,
    }),
  );
  const summary = summaryQuery.data;
  const workTypeTitle = getWorkTypeTitle(dcrTypeId);
  const includesExpenses = dcrTypeId === undefined;
  const dcrColumns = useMemo(() => getWorkSummaryDcrColumns(), []);
  const expenseColumns = useMemo(() => getWorkSummaryExpenseColumns(), []);

  const tabs: TableTab<WorkSummaryTab>[] = [
    { id: 'dcr', label: 'DCR Entries', count: summary?.dcr.length },
    { id: 'expenses', label: 'Expenses', count: summary?.expenses.length },
  ];

  const sharedLayout = {
    title: summaryQuery.isLoading ? undefined : getPageTitle(summary?.profile.name, workTypeTitle),
    titleAddon: (
      <DateRangeBadge
        dateFrom={period.dateFrom ?? summary?.period.dateFrom}
        dateTo={period.dateTo ?? summary?.period.dateTo}
      />
    ),
    onNoHistory,
    stats: getWorkSummaryStatCards(summary?.totals, includesExpenses),
    tableTabs: includesExpenses ? (
      <TableTabs
        tabs={tabs}
        activeTabId={activeTab}
        onTabChange={setActiveTab}
        ariaLabel="Work summary tables"
      />
    ) : undefined,
  };
  const sharedTable = {
    isLoading: summaryQuery.isLoading,
    error: summaryQuery.error,
    errorTitle: ERROR_TITLE,
    onRetry: () => void summaryQuery.refetch(),
    stickyColumnId: 'date',
  };

  if (includesExpenses && activeTab === 'expenses') {
    return (
      <DetailTableLayout
        {...sharedLayout}
        table={{
          ...sharedTable,
          rows: summary?.expenses ?? [],
          columns: expenseColumns,
          emptyMessage: 'No expenses in this period',
        }}
      />
    );
  }

  return (
    <DetailTableLayout
      {...sharedLayout}
      table={{
        ...sharedTable,
        rows: summary?.dcr ?? [],
        columns: dcrColumns,
        emptyMessage: 'No DCR entries in this period',
      }}
    />
  );
}
